import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';

import useLanguage from '@hooks/useLanguage';
import { styled } from '@spazious/react-styles';
import parseDate from '@utils/parseDate';

import DateRangePickerInput from './DateRangePickerInput.js';

import '~react-datepicker/dist/react-datepicker.css';

export interface DateRangePickerProps
  extends Omit<
    ReactDatePickerProps<never, true>,
    | 'value'
    | 'selectsRange'
    | 'popperProps'
    | 'startDate'
    | 'selected'
    | 'endDate'
    | 'onChange'
    | 'portalId'
    | 'customInput'
  > {
  value?: string | null;
  borderless?: boolean;
  onChange?: (evt: React.SyntheticEvent<any, Event> | undefined, dateRange: string | null) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * The date range picker is a controlled component, so we need to keep track of the selected dates
 * in the parent component. We can use the `onChange` prop to get notified when the user selects
 * a new date range
 *
 * TODO serialize the date range to a string
 */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  open: openProp,
  className,
  value,
  onChange,
  onOpen,
  onClose,
  borderless,
  minDate,
  ...restProps
}) => {
  const [stateOpen, setStateOpen] = useState(false);
  const { locale } = useLanguage();

  const handleOpen = useCallback(() => {
    if (openProp === undefined) {
      setStateOpen(true);
    } else {
      onOpen?.();
    }
  }, [onOpen, openProp]);

  const handleClose = useCallback(() => {
    if (openProp === undefined) {
      setStateOpen(false);
    } else {
      onClose?.();
    }
  }, [onClose, openProp]);

  const handleRangeChange: ReactDatePickerProps<never, true>['onChange'] = (dates, evt) => {
    const [startDate, endDate] = Array.isArray(dates) ? dates : [null, null];

    if (onChange) {
      onChange(evt, [startDate?.toJSON() ?? '', endDate?.toJSON() ?? ''].join('/'));
    }

    if (endDate) {
      handleClose();
    }
  };

  const open = openProp ?? stateOpen;
  const dateRange = value?.split('/');
  const startDate = dateRange?.[0] ? dayjs(dateRange[0]).toDate() : null;
  const endDate = dateRange?.[1] ? dayjs(dateRange[1]).toDate() : null;

  return (
    <DatePicker
      selectsRange
      customInput={
        <DateRangePickerInput
          borderless={borderless}
          endDate={endDate}
          inputStyle={{ color: 'var(--primary-text)' }}
          locale={locale}
          startDate={startDate}
        />
      }
      endDate={endDate}
      locale={locale}
      minDate={minDate ?? parseDate(new Date()).add(1, 'day').toDate()}
      open={open}
      popperClassName={clsx(className, 'popper')}
      popperPlacement="bottom"
      popperProps={{ strategy: 'fixed' }}
      portalId="root-portal"
      selected={startDate}
      shouldCloseOnSelect={false}
      showPopperArrow={false}
      startDate={startDate}
      wrapperClassName={clsx(className, 'wrapper')}
      onCalendarClose={handleClose}
      onCalendarOpen={handleOpen}
      onChange={handleRangeChange}
      onInputClick={handleOpen}
      {...restProps}
    />
  );
};

export default styled(DateRangePicker)`
  &.popper {
    z-index: 9999;
  }
`;
